import React, { useState } from 'react';

import { Field } from '@buildoutinc/blueprint-react/ui/Field';
import { Input } from '@buildoutinc/blueprint-react/ui/Input';
import { Modal } from '@buildoutinc/blueprint-react/ui/Modal';

import { Alert } from 'components/ui/alert';
import { Button } from 'components/ui/button';
import { deleteRequest, post, put } from 'helpers/api';
import { pluralize } from 'helpers/formatters/string_formats';

import { reasonFrom } from './api_reason';
import DocumentPicker from './page/document_picker';
import OrientationBadge from './orientation_badge';
import PdfDropzone, { DROP_REJECTED } from './pdf_dropzone';
import type { CandidateDocument } from './page/types';
import { TemplateType, type Orientation } from './types';

// helpers/api camelCases every response, but DocumentPicker and the detail view
// speak the endpoint's snake_case, so candidates are mapped back on the way in.
interface UploadedCandidate {
  id: number;
  name: string;
  documentType: string | null;
  orientation: string | null;
  inDocument: boolean;
}

interface UploadedPage {
  id: number;
  name: string;
  orientation: Orientation;
  sizeLabel: string | null;
  pageCount: number | null;
}

interface Props {
  basePath: string;
  companyId: number | null;
  show: boolean;
  onClose: (libraryChanged: boolean) => void;
  onUploaded: (pageName: string, documentCount: number) => void;
}

const UPLOAD_FAILED = "That file couldn't be uploaded. Check it's a PDF under 50MB and try again.";
const NAME_REQUIRED = 'Give the page a name before adding it.';
const SAVE_FAILED = 'There was an error saving your page, please try again.';

const pagesPhrase = (count: number | null) => (count ? pluralize(count, 'page') : null);

const toCandidate = (doc: UploadedCandidate): CandidateDocument => ({
  id: doc.id,
  name: doc.name,
  document_type: doc.documentType,
  orientation: doc.orientation,
  in_document: doc.inDocument,
});

export default function UploadModal({ basePath, companyId, show, onClose, onUploaded }: Props) {
  const [page, setPage] = useState<UploadedPage | null>(null);
  const [documents, setDocuments] = useState<CandidateDocument[]>([]);
  const [pageName, setPageName] = useState('');
  const [userLabel, setUserLabel] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const companyQuery = companyId ? `?company_id=${companyId}` : '';
  const membershipsUrl = `${basePath.replace(/\/pages$/, '')}/documents${companyQuery}`;

  // The row already exists once step two is reached, so cancel removes it again
  // rather than leaving a page named after the raw file. If that delete fails
  // the refetch at least makes the stray page visible instead of hiding it.
  const close = async () => {
    if (busy) return;

    if (page) {
      setBusy(true);
      try {
        await deleteRequest(`${basePath}/pdf/${page.id}${companyQuery}`);
      } catch {
        // Fall through to the refetch.
      } finally {
        setBusy(false);
      }
    }
    onClose(page !== null);
  };

  const handleDrop = async (files: File[]) => {
    const file = files[0];
    if (!file) return;

    const body = new FormData();
    body.append('pdf', file);

    setBusy(true);
    setError(null);
    try {
      const response: { page: UploadedPage; documents: UploadedCandidate[] } =
        await post(`${basePath}${companyQuery}`, body);
      setPage(response.page);
      setDocuments((response.documents || []).map(toCandidate));
      setPageName(response.page.name);
    } catch (requestError) {
      setError(reasonFrom(requestError) ?? UPLOAD_FAILED);
    } finally {
      setBusy(false);
    }
  };

  const confirm = async (documentIds: number[]) => {
    if (!page) return;
    if (pageName.trim() === '') {
      setError(NAME_REQUIRED);
      return;
    }

    setBusy(true);
    setError(null);
    try {
      await put(`${basePath}/pdf/${page.id}${companyQuery}`, {
        pageDefaults: { pageName, pageTemplateUserLabel: userLabel },
      });
      if (documentIds.length > 0) {
        await post(membershipsUrl, {
          pageType: TemplateType.Pdf,
          pageId: page.id,
          documentTemplateIds: documentIds,
        });
      }
      onUploaded(pageName, documentIds.length);
    } catch {
      setError(SAVE_FAILED);
    } finally {
      setBusy(false);
    }
  };

  // Step two lives in DocumentPicker's own modal so the checklist, search and
  // orientation rules stay in one place.
  if (page) {
    return (
      <DocumentPicker
        allowEmpty
        documents={documents}
        mode="add"
        pageOrientation={page.orientation}
        saving={busy}
        show={show}
        submitLabel="Add page"
        onClose={close}
        onSubmit={confirm}
      >
        <div className="mb-4">
          <div className="d-flex align-items-center gap-2 mb-3">
            <span className="fw-semibold">{page.name}</span>
            <OrientationBadge orientation={page.orientation} />
            {page.sizeLabel && <span className="text-muted">{page.sizeLabel}</span>}
            {pagesPhrase(page.pageCount) && (
              <span className="text-muted">{pagesPhrase(page.pageCount)} in one library page</span>
            )}
          </div>
          {error && (
            <Alert className="mb-3" severity="destructive">
              <Alert.Title>{error}</Alert.Title>
            </Alert>
          )}
          <div className="row">
            <div className="col-md-6">
              <Field>
                <Field.Label>Page name</Field.Label>
                <Input
                  type="text"
                  value={pageName}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPageName(e.target.value)}
                />
              </Field>
            </div>
            <div className="col-md-6">
              <Field>
                <Field.Label>Page template label</Field.Label>
                <Input
                  type="text"
                  value={userLabel}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setUserLabel(e.target.value)}
                />
                <Field.Description>Internal only.</Field.Description>
              </Field>
            </div>
          </div>
        </div>
      </DocumentPicker>
    );
  }

  return (
    <Modal open={show} onClose={close}>
      <Modal.Content centered>
        <Modal.Header>
          <Modal.Title>Upload a PDF</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && (
            <Alert className="mb-3" severity="destructive">
              <Alert.Title>{error}</Alert.Title>
            </Alert>
          )}
          <PdfDropzone
            busy={busy}
            busyLabel="Uploading..."
            hint="PDFs up to 50MB"
            prompt={<>Drop a file here or <span className="text-primary">click to upload</span></>}
            onDrop={handleDrop}
            onDropRejected={() => setError(DROP_REJECTED)}
          />
          <p className="text-muted mt-3 mb-0">
            However many pages the PDF has, it becomes one page in the library. It is added whole,
            and there is no way to split it.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button disabled={busy} variant="ghost" onClick={close}>Cancel</Button>
        </Modal.Footer>
      </Modal.Content>
    </Modal>
  );
}
